import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Checkbox } from '@mui/material';
import { BsThreeDots } from 'react-icons/bs';
import { LuEye } from 'react-icons/lu';
import { toast } from 'react-toastify';

import useDebounce from '../../CustomHooks/useDebounce';
import { MSG_ERROR } from '../../utils/contants';
import { apiHeader, BaseURL } from '../../config/apiUrl';
import { Get, Delete } from '../../Axios/AxiosFunctions';
import { Button } from '../../Component/Button/Button';
import TableSkeleton from '../../Component/TableSkeleton';
import NoData from '../../Component/NoData/NoData';
import PoperComponent from '../../Component/PopperComponent';
import SearchInput from '../../Component/SearchInput';
import AreYouSureModal from '../../modals/AreYouSureModal';
import EmailPreviewModal from '../../modals/EmailPreviewModal';
import classes from './AutomatedEmails.module.css';

const recordsLimit = 40;

const AutomatedEmailsList = () => {
  const navigate = useNavigate();

  const { user, access_token: accessToken, company_id: companyId } = useSelector(
    (state) => state?.authReducer,
  );

  const superAdminEmail = process.env.REACT_APP_SUPER_ADMIN_EMAIL;
  const baseApiUrl = BaseURL(`automated-email-template`);

  const [isFetchingTemplates, setIsFetchingTemplates] = useState(true);

  const [page] = useState(1);
  const [templateData, setTemplateData] = useState(null);
  const [openPopper, setOpenPopper] = useState(false);
  const [isOpenEmailPreview, setIsOpenEmailPreview] = useState(false);
  const anchorRef = useRef(null);
  const [indexMap, setIndexMap] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearchTerm = useDebounce(search, 500);

  const getAllEmailTemplates = async () => {
    if (accessToken && accessToken?.length > 0) {
      const url = `${baseApiUrl}?search=${search}&page=${page}&limit=${recordsLimit}`;

      const response = await Get(url, accessToken, companyId);

      if (response?.status === 200) {
        setTemplateData(response?.data);
      }

      setIsFetchingTemplates(false);
    }
  };

  const handleActionClick = (flag) => {
    if (flag == 'Delete') {
      setOpenDeleteConfirmation(true);
    } else if (flag == 'Edit') {
      navigate(`/edit-automated-email/${selectedItem?._id}`);
    }
  };

  const handleDelete = async () => {
    const response = await Delete(
      `${baseApiUrl}/${selectedItem?._id}`,
      apiHeader(accessToken),
    );

    if (response?.status === 200) {
      toast.success('Deleted successfully.');
      getAllEmailTemplates();
      setSelectedItem(null);
      setOpenDeleteConfirmation(false);
    } else {
      toast.error(response.data || MSG_ERROR);
    }
  };

  const handleToggle = () => {
    setOpenPopper((prevOpen) => !prevOpen);
  };

  useEffect(() => {
    getAllEmailTemplates();
  }, [accessToken, debouncedSearchTerm]);

  return (
    <>
      <div
        className={[classes.headerContainer]}
        style={{ justifyContent: 'flex-end' }}
      >
        <div className={[classes.inputDiv]}>
          <SearchInput
            setter={setSearch}
            value={search}
            placeholder={'Search'}
          />

          {user?.email === superAdminEmail && (
            <Button
              label="Add Template"
              className={classes.addProjectBtn}
              onClick={() => {
                navigate('/add-automated-email');
              }}
            />
          )}
        </div>
      </div>

      <div className="table100 ver1 m-b-110">
        <div className="table100-head">
          <table>
            <thead>
              <tr className="row100 head">
                <th
                  className="cell100 column1"
                  style={{ width: '5%', textAlign: 'start' }}
                >
                  S.No
                </th>
                <th
                  className="cell100 column1"
                  style={{ width: '15%', textAlign: 'start' }}
                >
                  Trigger
                </th>
                <th
                  className="cell100 column2"
                  style={{ width: '15%', textAlign: 'start' }}
                >
                  From Email
                </th>
                <th
                  className="cell100 column3"
                  style={{ width: '15%', textAlign: 'start' }}
                >
                  To Email
                </th>
                <th
                  className="cell100 column4"
                  style={{ width: '10%', textAlign: 'start' }}
                >
                  CC
                </th>
                <th
                  className="cell100 column5"
                  style={{ width: '10%', textAlign: 'center' }}
                >
                  Subject
                </th>
                <th
                  className="cell100 column6"
                  style={{ width: '10%', textAlign: 'center' }}
                >
                  Email Body
                </th>
                <th
                  className="cell100 column7"
                  style={{ width: '10%', textAlign: 'center' }}
                >
                  Active
                </th>
                <th
                  className="cell100 column9"
                  style={{ width: '8%', textAlign: 'center' }}
                >
                  Link
                </th>
                {user?.email === superAdminEmail && (
                  <th
                    className="cell100 column10"
                    style={{ width: '5%', textAlign: 'center' }}
                  >
                    Action
                  </th>
                )}
              </tr>
            </thead>
          </table>
        </div>
        {isFetchingTemplates ? (
          <TableSkeleton rowsCount={recordsLimit} colsCount={10} />
        ) : (
          <div className="table100-body js-pscroll ps ps--active-y">
            <table>
              <tbody>
                {templateData?.result?.length > 0 ? (
                  templateData?.result?.map((item, index) => (
                    <tr
                      key={item?._id}
                      className="row100 body"
                      style={{
                        backgroundColor:
                          selectedItem?._id == item?._id && '#E5F5FF',
                      }}
                    >
                      <td
                        className="cell100 column1"
                        style={{ width: '5%', textAlign: 'start' }}
                      >
                        {page == 1
                          ? index + 1
                          : recordsLimit * (page - 1) + index + 1}
                      </td>
                      <td
                        className="cell100 column1"
                        style={{ width: '15%', textAlign: 'start' }}
                      >
                        {item?.trigger}
                      </td>
                      <td
                        className="cell100 column3"
                        style={{
                          width: '15%',
                          textAlign: 'start',
                          maxWidth: '15%',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                        }}
                      >
                        {item?.from}
                      </td>
                      <td
                        className="cell100 column4"
                        style={{
                          width: '15%',
                          textAlign: 'start',
                          maxWidth: '15%',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                        }}
                      >
                        {item?.to}
                      </td>
                      <td
                        className="cell100 column5"
                        style={{
                          width: '10%',
                          textAlign: 'center',
                          maxWidth: '10%',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                        }}
                      >
                        {item?.cc}
                      </td>
                      <td
                        className="cell100 column6"
                        style={{ width: '10%', textAlign: 'center' }}
                      >
                        <div style={{ position: 'relative' }}>
                          {item?.subject}
                        </div>
                      </td>
                      <td
                        className="cell100 column7"
                        style={{ width: '10%', textAlign: 'center' }}
                      >
                        <div
                          style={{ position: 'relative', cursor: 'pointer' }}
                          onClick={() => {
                            setSelectedItem(item);
                            setIsOpenEmailPreview(true);
                          }}
                        >
                          <LuEye size={25} />
                        </div>
                      </td>
                      <td
                        className="cell100 column8"
                        style={{ width: '10%', textAlign: 'center' }}
                      >
                        <div style={{ position: 'relative' }}>
                          <Checkbox checked={item?.isEnabled} disabled={true} />
                        </div>
                      </td>
                      <td
                        className="cell100 column10"
                        style={{ width: '8%', textAlign: 'center' }}
                      >
                        <div style={{ position: 'relative' }}>{item?.link}</div>
                      </td>

                      {user?.email === superAdminEmail && (
                        <td
                          className="cell100 column11"
                          style={{ width: '5%', textAlign: 'center' }}
                        >
                          <div
                            ref={index == indexMap ? anchorRef : null}
                            id="composition-button"
                            aria-controls={
                              openPopper ? 'composition-menu' : undefined
                            }
                            aria-expanded={openPopper ? 'true' : undefined}
                            aria-haspopup="true"
                            onClick={() => {
                              setSelectedItem(item);
                              setIndexMap(index);
                              setTimeout(() => {
                                handleToggle();
                              }, 100);
                            }}
                          >
                            <BsThreeDots
                              size={30}
                              className={[classes.threeDots]}
                            />
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <NoData text={'No Automated Emails Found'} />
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PoperComponent
        handleClick={handleActionClick}
        open={openPopper}
        setOpen={setOpenPopper}
        anchorRef={anchorRef}
        data={['Edit', 'Delete']}
      />

      <AreYouSureModal
        show={openDeleteConfirmation}
        setShow={setOpenDeleteConfirmation}
        subTitle={`Are you sure you want delete this template`}
        isApiCall={false}
        onClick={() => handleDelete()}
      />

      <EmailPreviewModal
        show={isOpenEmailPreview}
        setShow={setIsOpenEmailPreview}
        data={selectedItem?.emailBody}
      />
    </>
  );
};

export default AutomatedEmailsList;
